<template>
  <div class="min-h-screen flex items-center justify-center relative overflow-hidden bg-primary-500 dark:bg-surface-950 px-4 sm:px-6 lg:px-8 py-12 font-sans">

    <!-- =============================================
         Animated Background Orbs
         ============================================= -->
    <div class="absolute inset-0 overflow-hidden">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
    </div>

    <!-- Subtle grid overlay -->
    <div class="absolute inset-0 bg-pattern-dots opacity-20"></div>

    <!-- =============================================
         Main Content
         ============================================= -->
    <div class="relative z-10 w-full max-w-md space-y-8 animate-fade-up">

      <!-- Logo and Title -->
      <div class="text-center">
        <div class="flex justify-center mb-6">
          <div class="relative">
            <div class="absolute -inset-4 bg-accent-500/20 rounded-full blur-2xl animate-pulse-soft"></div>
            <img
              src="../../assets/earth.png"
              alt="Neutral Fuels"
              class="relative h-24 w-24 rounded-full shadow-accent-glow ring-2 ring-white/10 animate-float"
            />
          </div>
        </div>
        <h1 class="text-display-sm text-white mb-3">
          Reset Password
        </h1>
        <p class="text-primary-200 dark:text-surface-400 text-base leading-relaxed max-w-sm mx-auto">
          Enter your email address and we'll send you instructions to reset your password.
        </p>
      </div>

      <!-- Form Card -->
      <div class="card-glass p-8 sm:p-10 space-y-6">
        <form @submit.prevent="handleReset" class="space-y-6">

          <!-- Email Input -->
          <div>
            <label for="email" class="label">
              Email Address
            </label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <EnvelopeIcon class="h-5 w-5 text-surface-400 group-focus-within:text-accent-500 transition-colors duration-200" />
              </div>
              <input
                id="email"
                v-model="email"
                type="email"
                autocomplete="email"
                required
                class="input pl-12 py-3.5 text-base"
                placeholder="you@example.com"
                :disabled="isPending"
              />
            </div>
          </div>

          <!-- Success Message -->
          <transition
            enter-active-class="transition duration-300 ease-spring"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
          >
            <div
              v-if="successMessage"
              class="bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-800/50 rounded-xl p-4 flex items-start space-x-3"
            >
              <CheckCircleIcon class="h-5 w-5 text-accent-500 flex-shrink-0 mt-0.5" />
              <p class="text-sm text-accent-700 dark:text-accent-300">{{ successMessage }}</p>
            </div>
          </transition>

          <!-- Error Message -->
          <transition
            enter-active-class="transition duration-300 ease-spring"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
          >
            <div
              v-if="error"
              class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl p-4 flex items-start space-x-3"
            >
              <ExclamationCircleIcon class="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p class="text-sm text-red-700 dark:text-red-300">{{ error }}</p>
            </div>
          </transition>

          <!-- Submit Button -->
          <BaseButton
            type="submit"
            variant="primary"
            size="lg"
            class="w-full"
            :loading="isPending"
            :disabled="!email"
          >
            {{ isPending ? 'Sending...' : 'Send Reset Link' }}
          </BaseButton>

          <!-- Back to Login -->
          <div class="text-center pt-2">
            <router-link
              to="/login"
              class="inline-flex items-center gap-2 text-sm font-semibold text-accent-400 hover:text-accent-300 transition-colors duration-200"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              Back to Sign In
            </router-link>
          </div>
        </form>
      </div>

      <!-- Footer -->
      <div class="text-center text-sm text-white/50 space-y-1">
        <p>Powered by <span class="font-semibold text-white/70">Neutral Fuels</span></p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { sendPasswordResetEmail } from 'firebase/auth'
import { auth } from '@/firebase/config'
import BaseButton from '@/components/ui/BaseButton.vue'
import {
  EnvelopeIcon,
  ExclamationCircleIcon,
  CheckCircleIcon
} from '@heroicons/vue/24/outline'

const email = ref('')
const isPending = ref(false)
const error = ref(null)
const successMessage = ref(null)

const handleReset = async () => {
  error.value = null
  successMessage.value = null
  isPending.value = true

  try {
    await sendPasswordResetEmail(auth, email.value)
    successMessage.value = 'Password reset email sent! Please check your inbox.'
    email.value = ''
  } catch (err) {
    if (err.code === 'auth/user-not-found') {
      error.value = 'No account found with this email address.'
    } else if (err.code === 'auth/invalid-email') {
      error.value = 'Please enter a valid email address.'
    } else {
      error.value = 'Failed to send reset email. Please try again.'
    }
  } finally {
    isPending.value = false
  }
}
</script>

<style scoped>
/* ---- Animated Gradient Orbs ---- */
.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  will-change: transform;
}

.orb-1 {
  width: 450px;
  height: 450px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.4) 0%, transparent 70%);
  top: -15%;
  right: -10%;
  animation: orbFloat1 14s ease-in-out infinite;
}

.orb-2 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(6, 182, 212, 0.35) 0%, transparent 70%);
  bottom: -10%;
  left: -5%;
  animation: orbFloat2 18s ease-in-out infinite;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(0, 58, 109, 0.3) 0%, transparent 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: orbFloat3 12s ease-in-out infinite;
}

@keyframes orbFloat1 {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(-50px, 40px) scale(1.1);
  }
  50% {
    transform: translate(-20px, 70px) scale(0.95);
  }
  75% {
    transform: translate(30px, 20px) scale(1.05);
  }
}

@keyframes orbFloat2 {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(40px, -30px) scale(1.05);
  }
  50% {
    transform: translate(70px, -50px) scale(1.1);
  }
  75% {
    transform: translate(20px, -20px) scale(0.95);
  }
}

@keyframes orbFloat3 {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
  }
  33% {
    transform: translate(-40%, -60%) scale(1.1);
  }
  66% {
    transform: translate(-60%, -40%) scale(0.9);
  }
}

/* Dark mode orb adjustments */
:deep(.dark) .orb {
  opacity: 0.3;
}
</style>
